import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Instagram, ArrowRight, Camera, Sparkles } from 'lucide-react';

export default function PremiumGraduationSite() {
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsLoaded(true);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

const photos = [
  { id: 1, title: "1", subtitle: "", url: "/photos/LeonHalipPhotography10297.JPG" },
  { id: 2, title: "2", subtitle: "", url: "/photos/LeonHalipPhotography10298.JPG" },
  { id: 3, title: "3", subtitle: "", url: "/photos/LeonHalipPhotography10299.JPG" },
  { id: 4, title: "4", subtitle: "", url: "/photos/LeonHalipPhotography10300.JPG" },
  { id: 5, title: "5", subtitle: "", url: "/photos/LeonHalipPhotography10301.JPG" },
  { id: 6, title: "6", subtitle: "", url: "/photos/LeonHalipPhotography10303.JPG" },
  { id: 7, title: "7", subtitle: "", url: "/photos/LeonHalipPhotography10304.JPG" },
  { id: 8, title: "8", subtitle: "", url: "/photos/LeonHalipPhotography10305.JPG" },
  { id: 9, title: "9", subtitle: "", url: "/photos/LeonHalipPhotography10306.JPG" },
  { id: 10, title: "10", subtitle: "", url: "/photos/LeonHalipPhotography10307.JPG" },
  { id: 11, title: "11", subtitle: "", url: "/photos/LeonHalipPhotography10308.JPG" },
];


  const nextPhoto = () => {
    setCurrentPhoto((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhoto((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Ambient lighting effect */}
      <div 
        className="fixed inset-0 opacity-20 pointer-events-none transition-opacity duration-1000"
        style={{
          background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(255,255,255,0.1), transparent 40%)`
        }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-3xl bg-black/40 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="text-xl font-light tracking-wider text-white/90">
              Graduation 2025
            </div>
            <div className="flex space-x-12 text-sm font-light">
              <button onClick={() => document.getElementById('photos').scrollIntoView({ behavior: 'smooth' })} className="text-white/70 hover:text-white transition-colors duration-500">Photos</button>
              <button onClick={() => document.getElementById('thoughts').scrollIntoView({ behavior: 'smooth' })} className="text-white/70 hover:text-white transition-colors duration-500">Thoughts</button>
              <button onClick={() => document.getElementById('connect').scrollIntoView({ behavior: 'smooth' })} className="text-white/70 hover:text-white transition-colors duration-500">Connect</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="h-screen flex items-center justify-center relative overflow-hidden">
        <div className={`text-center z-10 transform transition-all duration-2000 ease-out ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
          {/* Profile Image */}
          <div className="relative mb-16 group">
            <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 flex items-center justify-center relative overflow-hidden">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-3xl border border-white/10">
                🎓
              </div>
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            </div>
          </div>

          <h1 className="text-7xl md:text-8xl font-thin mb-8 tracking-tight">
            <span className="bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent">
              Graduated
            </span>
          </h1>
          
          <div className="space-y-4 mb-16">
            <p className="text-2xl md:text-3xl font-extralight text-white/80 tracking-wide">
              Majoring in Computer Science

            </p>
            <p className="text-lg font-light text-white/60 tracking-wider">
                            Ethan Ball • Class of 2025

            </p>
          </div>

          <button 
            onClick={() => document.getElementById('photos').scrollIntoView({ behavior: 'smooth' })}
            className="group inline-flex items-center space-x-3 bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/20 hover:border-white/40 px-8 py-4 rounded-full transition-all duration-700 hover:scale-105"
          >
            <span className="font-light tracking-wide">View Photos</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>

        {/* Floating elements */}
        <div className="absolute top-1/4 left-1/4 w-px h-32 bg-gradient-to-b from-white/20 to-transparent rotate-45 animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-px h-24 bg-gradient-to-b from-white/20 to-transparent -rotate-45 animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-white/30 rounded-full animate-pulse delay-500"></div>
      </section>


      {/*Intro Section*/}
      <section className="pt-32 pb-20 px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`transform transition-all duration-2000 ease-out ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
            <h2 className="text-4xl md:text-5xl font-thin mb-8 tracking-tight text-white/90">
              Welcome to My Celebration
            </h2>
            <div className="text-lg md:text-xl font-light text-white/70 leading-relaxed space-y-6">
              <p>
                Thank you for celebrating at my party with me. I created this web page with links to the food for thought questions at your table, and to share a few other things.
              </p>
              <p>
                Please connect with me or follow me on my LinkedIn, Github or Instagram via the links at the bottom of the page.
              </p>
            </div>
          </div>
        </div>
      </section>
      

      {/* Photo Gallery */}
      <section id="photos" className="py-32 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-thin mb-6 tracking-tight text-white/90">
              Moments
            </h2>
            <p className="text-lg font-light text-white/60 max-w-2xl mx-auto leading-relaxed">
              Senior Pic's
            </p>
          </div>

          {/* Main Photo Display */}
          <div className="mb-16">
            <div className="relative bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm rounded-3xl p-8 border border-white/10 group">
              <div className="aspect-video bg-gradient-to-br from-gray-900 to-black rounded-2xl flex items-center justify-center relative overflow-hidden">
<img 
  src={photos[currentPhoto].url} 
  alt={photos[currentPhoto].title} 
  className="object-contain w-full h-full absolute inset-0"
/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <h3 className="text-2xl font-light mb-2 text-white">
                    {photos[currentPhoto].title}
                  </h3>
                  <p className="text-white/70 font-light">
                    {photos[currentPhoto].subtitle}
                  </p>
                </div>
              </div>
              
              {/* Navigation arrows */}
              <button 
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 hover:border-white/40 transition-all duration-300 opacity-0 group-hover:opacity-100"
              >
                <ArrowRight className="w-5 h-5 rotate-180" />
              </button>
              <button 
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 hover:border-white/40 transition-all duration-300 opacity-0 group-hover:opacity-100"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Photo indicators */}
            <div className="flex justify-center mt-8 space-x-3">
              {photos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPhoto(index)}
                  className={`transition-all duration-500 ${
                    currentPhoto === index 
                      ? 'w-8 h-2 bg-white rounded-full' 
                      : 'w-2 h-2 bg-white/30 rounded-full hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Photo Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                onClick={() => setCurrentPhoto(index)}
                className={`group relative aspect-square bg-gradient-to-br from-white/5 to-white/2 rounded-2xl overflow-hidden border transition-all duration-700 hover:scale-105 ${
                  currentPhoto === index 
                    ? 'border-white/40 shadow-2xl shadow-white/10' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
<img 
  src={photo.url} 
  alt={photo.title} 
  className="w-full absolute inset-0"
/>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-xs font-light text-white/90 truncate">
                    {photo.title}
                  </p>
                </div>
                {currentPhoto === index && (
                  <div className="absolute inset-0 border-2 border-white/60 rounded-2xl"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Food for Thought Section */}
      <section id="thoughts" className="py-32 px-8 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-thin mb-6 tracking-tight text-white/90">
              Random Thoughts
            </h2>
            <p className="text-lg font-light text-white/60 max-w-2xl mx-auto leading-relaxed">
              Food for thought questions from your table
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">What if the universe was in a black hole?</h3>
              <p className="text-white/60 font-light text-sm mb-4">A fascinating theoretical concept worth exploring...</p>
              <a href="https://www.space.com/space-exploration/james-webb-space-telescope/is-our-universe-trapped-inside-a-black-hole-this-james-webb-space-telescope-discovery-might-blow-your-mind" className="text-white/50 hover:text-white/80 text-xs font-light tracking-wide transition-colors duration-300">
                EXPLORE →
              </a>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">T/F: There's a chance life exists on K2-18b</h3>
              <p className="text-white/60 font-light text-sm mb-4">
                <span className="text-green-400">True:</span> Scientist are 99.7% confident that the planets atmosphere contains a certain chemical which is only produced by living organisms.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">You have better chances winning the lottery several times than being born?</h3>
              <p className="text-white/60 font-light text-sm mb-4">
                <span className="text-green-400">True:</span> The odds of your specific existence are astronomically small
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">T/F: The foundation for wireless charging was invented in 1800's</h3>
              <p className="text-white/60 font-light text-sm mb-4">
                <span className="text-green-400">True:</span> Tesla's wireless power demonstrations laid the groundwork
              </p>
              <a href="https://en.wikipedia.org/wiki/World_Wireless_System#:~:text=Tesla%27s%20demonstrations%20of%20wireless%20power,591%20m%20from%20the%20transmitter" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white/80 text-xs font-light tracking-wide transition-colors duration-300">
                LEARN MORE →
              </a>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">T/F: There's a 12 mile wide asteroid in our solar system - A11pl3z</h3>
              <p className="text-white/60 font-light text-sm mb-4">
                <span className="text-green-400">True:</span> Currently tracking through our solar system
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">T/F: There's a 12 mile asteroid is traveling at 42 miles/second</h3>
              <p className="text-white/60 font-light text-sm mb-4">
                <span className="text-green-400">True:</span> In 1 hour, it can travel across 19 Earths
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">Fun fact: Dark matter passes through your body</h3>
              <p className="text-white/60 font-light text-sm mb-4">
              Many, perhaps even billions of dark matter particles pass through you every second
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700">
              <h3 className="text-xl font-light mb-4 text-white/90">T/F: Dark matter is the same as Anti-Matter</h3>
              <p className="text-white/60 font-light text-sm mb-4">
                <span className="text-green-400">False:</span> Both are independent forms of matter with unique properties
              </p>
              <a href="https://www.google.com/search?q=dark+matter+explained" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white/80 text-xs font-light tracking-wide transition-colors duration-300">
                EXPLORE →
              </a>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm p-8 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-700 md:col-span-2">
              <h3 className="text-xl font-light mb-4 text-white/90">C₁₂H₂₂O₁₁ + C₇H₈N₄O₂ + Corylus avellana = ?</h3>
              <p className="text-white/60 font-light text-sm mb-4">
                <span className="text-green-400">Answer:</span> Sugar + Chocolate (technically theobromine) + Hazelnut = Nutella
              </p>
              <p className="text-white/50 font-light text-xs">
                The chemical formula for everyone's favorite spread!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Connect Section */}
      <section id="connect" className="py-32 px-8 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-thin mb-6 tracking-tight text-white/90">
              Connect
            </h2>
            <p className="text-lg font-light text-white/60 max-w-2xl mx-auto leading-relaxed">
              Let's connect!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <a
              href="https://www.linkedin.com/in/ethan-b-4aa220337/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-white/5 to-white/2 hover:from-white/10 hover:to-white/5 backdrop-blur-sm p-10 rounded-3xl border border-white/10 hover:border-white/30 transition-all duration-700 hover:scale-105 hover:shadow-2xl hover:shadow-white/5"
            >
              <Linkedin className="w-10 h-10 text-white/80 mb-8 group-hover:scale-110 transition-transform duration-500" />
              <h3 className="text-xl font-light mb-3 text-white/90">LinkedIn</h3>
              <p className="text-white/60 font-light text-sm leading-relaxed">
                Professional network and career opportunities
              </p>
              <div className="mt-6 flex items-center text-white/50 group-hover:text-white/80 transition-colors duration-500">
                <span className="text-xs font-light tracking-wide">CONNECT</span>
                <ArrowRight className="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </a>

            <a
              href="https://github.com/ebspark"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-white/5 to-white/2 hover:from-white/10 hover:to-white/5 backdrop-blur-sm p-10 rounded-3xl border border-white/10 hover:border-white/30 transition-all duration-700 hover:scale-105 hover:shadow-2xl hover:shadow-white/5"
            >
              <Github className="w-10 h-10 text-white/80 mb-8 group-hover:scale-110 transition-transform duration-500" />
              <h3 className="text-xl font-light mb-3 text-white/90">GitHub</h3>
              <p className="text-white/60 font-light text-sm leading-relaxed">
                Code repositories and technical projects
              </p>
              <div className="mt-6 flex items-center text-white/50 group-hover:text-white/80 transition-colors duration-500">
                <span className="text-xs font-light tracking-wide">EXPLORE</span>
                <ArrowRight className="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </a>

            <a
              href="https://instagram.com/ethan_.ball"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-white/5 to-white/2 hover:from-white/10 hover:to-white/5 backdrop-blur-sm p-10 rounded-3xl border border-white/10 hover:border-white/30 transition-all duration-700 hover:scale-105 hover:shadow-2xl hover:shadow-white/5"
            >
              <Instagram className="w-10 h-10 text-white/80 mb-8 group-hover:scale-110 transition-transform duration-500" />
              <h3 className="text-xl font-light mb-3 text-white/90">Instagram</h3>
              <p className="text-white/60 font-light text-sm leading-relaxed">
                Life moments and creative inspiration
              </p>
              <div className="mt-6 flex items-center text-white/50 group-hover:text-white/80 transition-colors duration-500">
                <span className="text-xs font-light tracking-wide">FOLLOW</span>
                <ArrowRight className="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-8">
            <Sparkles className="w-6 h-6 text-white/40 mx-auto animate-pulse" />
          </div>
          <p className="text-white/40 font-light text-sm tracking-wider mb-4">
            © 2025 Ethan B.
          </p>
          <p className="text-white/30 font-light text-xs tracking-widest">
            GRADUATED • READY • INSPIRED
          </p>
        </div>
      </footer>
    </div>
  );
}